/**
 * Prescription Form Component
 *
 * Complete prescription creation form with all fields.
 * Adapted from healthchat for shadcn/ui.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type {
  DeliveryType,
  DosageFormat,
  ICD10Code,
  Medication,
  PrescriptionFormData,
  PrescriptionType,
  TimeUnit,
} from '@/types/prescription';
import { defaultPrescriptionFormData } from '@/types/prescription';
import { Check, ChevronDown, FileText, Loader2, Pill, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface PrescriptionFormProps {
  initialData?: Partial<PrescriptionFormData>;
  initialMedicationName?: string; // From AI-detected task title (e.g., "Parkódín 10 mg")
  initialDosageInfo?: string; // From AI-detected task details (e.g., "10 mg x 2 vegna hósta")
  onSubmit: (data: PrescriptionFormData) => void;
  onClose?: () => void;
}

export function PrescriptionForm({
  initialData,
  initialMedicationName,
  initialDosageInfo,
  onSubmit,
  onClose,
}: PrescriptionFormProps) {
  const [formData, setFormData] = useState<PrescriptionFormData>({
    ...defaultPrescriptionFormData,
    ...initialData,
  });
  const [medicationSearch, setMedicationSearch] = useState('');
  const [medicationOpen, setMedicationOpen] = useState(false);
  const [medicationResults, setMedicationResults] = useState<Medication[]>([]);
  const [isSearchingMedication, setIsSearchingMedication] = useState(false);
  const [hasAutoSearched, setHasAutoSearched] = useState(false);
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [diagnosisOpen, setDiagnosisOpen] = useState(false);
  const [diagnosisResults, setDiagnosisResults] = useState<ICD10Code[]>([]);
  const [isSearchingDiagnosis, setIsSearchingDiagnosis] = useState(false);

  // Common medication to condition mappings for inferring "Notað við"
  // Note: Uses dative case (þágufall) after "við"
  const medicationConditionMap: Record<string, string> = {
    // Blood pressure (háþrýstingur → háþrýstingi - dative)
    amlodipine: 'háþrýstingi',
    amlodipin: 'háþrýstingi',
    lisinopril: 'háþrýstingi',
    losartan: 'háþrýstingi',
    enalapril: 'háþrýstingi',
    ramipril: 'háþrýstingi',
    // Cholesterol (háu kólesteróli - dative)
    atorvastatin: 'háu kólesteróli',
    simvastatin: 'háu kólesteróli',
    rosuvastatin: 'háu kólesteróli',
    // Diabetes (sykursýki - same in accusative)
    metformin: 'sykursýki',
    insulin: 'sykursýki',
    // Pain/cough (hósti → hósta, verkir → verkjum)
    parkódin: 'hósta',
    parkodin: 'hósta',
    kodein: 'hósta',
    paracetamol: 'verkjum',
    ibuprofen: 'verkjum',
    naproxen: 'verkjum',
    // Antibiotics (sýking → sýkingu)
    amoxicillin: 'sýkingu',
    penicillin: 'sýkingu',
    azithromycin: 'sýkingu',
    // Acid reflux (brjóstsviði → brjóstsviða)
    omeprazole: 'brjóstsviða',
    pantoprazole: 'brjóstsviða',
    esomeprazole: 'brjóstsviða',
    // Mental health (þunglyndi - same in accusative)
    sertraline: 'þunglyndi',
    escitalopram: 'þunglyndi',
    fluoxetine: 'þunglyndi',
    // Allergies (ofnæmi - same in accusative)
    cetirizine: 'ofnæmi',
    loratadine: 'ofnæmi',
    // Asthma (astmi → astma)
    salbutamol: 'astma',
    budesonide: 'astma',
  };

  // Convert genitive case (eignarfall, used after "vegna") to dative (þágufall, used after "við")
  const genitiveToDative: Record<string, string> = {
    háþrýstings: 'háþrýstingi',
    'hás blóðþrýstings': 'háum blóðþrýstingi',
    háþrýstingsins: 'háþrýstingi',
    kólesteróls: 'kólesteróli',
    'hás kólesteróls': 'háu kólesteróli',
    sykursýki: 'sykursýki',
    sykursýkinnar: 'sykursýki',
    verkja: 'verkjum',
    höfuðverkja: 'höfuðverk',
    hósta: 'hósta',
    hóstans: 'hósta',
    sýkingar: 'sýkingu',
    sýkingarinnar: 'sýkingu',
    brjóstsviða: 'brjóstsviða',
    þunglyndis: 'þunglyndi',
    kvíða: 'kvíða',
    ofnæmis: 'ofnæmi',
    astma: 'astma',
    svefnleysis: 'svefnleysi',
  };

  // Convert extracted reason from genitive to dative case
  const convertToDative = (reason: string): string => {
    const lowerReason = reason.toLowerCase();
    if (genitiveToDative[lowerReason]) {
      return genitiveToDative[lowerReason];
    }
    // Return lowercase version if no conversion found
    return lowerReason;
  };

  // Extract "vegna X" or "fyrir X" from text
  const extractUsedFor = (text: string): string | null => {
    // Match "vegna [reason]" or "fyrir [reason]"
    // "vegna" takes genitive, but "notað við" needs dative, so we convert
    const vegnaMatch = text.match(/vegna\s+([^,.\d]+)/i);
    if (vegnaMatch) {
      const reason = vegnaMatch[1].trim();
      return convertToDative(reason);
    }
    const fyrirMatch = text.match(/fyrir\s+([^,.\d]+)/i);
    if (fyrirMatch) {
      const reason = fyrirMatch[1].trim();
      return convertToDative(reason);
    }
    return null;
  };

  // Infer condition from medication name
  const inferConditionFromMedication = (medicationName: string): string | null => {
    const lowerName = medicationName.toLowerCase();
    for (const [med, condition] of Object.entries(medicationConditionMap)) {
      if (lowerName.includes(med)) {
        return condition;
      }
    }
    return null;
  };

  // Parse dosage info from AI (e.g., "10 mg x 2 vegna hósta")
  const parseDosageInfo = (info: string) => {
    // Try to parse "X mg x Y" pattern where X is the dose (ignored, part of medication)
    // and Y is how many times per day. Quantity is always 1 unit per dose.
    const match = info.match(/\d+\s*(?:mg|mcg|ml|g)\s*x\s*(\d+)/i);
    if (match) {
      return {
        quantity: '1', // Always 1 unit per dose
        frequency: match[1], // Times per day
      };
    }
    return null;
  };

  // Extract form from medication name string like "Amlodipine (5 mg, töflur, 100.0 stk)"
  // or "Fucidin (Heilsa) (20 mg/g, smyrsli, 15.0 g)" - need to get the LAST parentheses
  const extractFormFromName = (name: string | undefined): string => {
    if (!name) return '';
    // Find ALL parentheses matches and use the last one (which has dose, form, quantity)
    const matches = name.match(/\(([^)]+)\)/g);
    if (matches && matches.length > 0) {
      // Get the last match (most likely the one with form info)
      const lastMatch = matches[matches.length - 1];
      // Remove the outer parentheses
      const content = lastMatch.slice(1, -1);
      const parts = content.split(',').map((p) => p.trim());
      // Form is usually the second part (index 1) if there are 3 parts
      if (parts.length >= 3) {
        return parts[1];
      }
    }
    return '';
  };

  // Get unit name based on medication form and quantity
  const getUnitName = (form: string | undefined, quantity: number): string => {
    const lowerForm = (form || '').toLowerCase();

    // Tablets: tafla (1), töflur (2+)
    // Matches: töflur, tafla, filmuhúðuð tafla, munn-töflur, etc.
    if (
      lowerForm.includes('töflu') ||
      lowerForm.includes('tafla') ||
      lowerForm.includes('tabl') ||
      lowerForm.includes('tablet')
    ) {
      return quantity === 1 ? 'tafla' : 'töflur';
    }
    // Capsules: hylki (same singular/plural)
    if (lowerForm.includes('hylki') || lowerForm.includes('caps')) {
      return 'hylki';
    }
    // Suppositories: stíll (1), stílar (2+)
    if (lowerForm.includes('stíl') || lowerForm.includes('suppositor') || lowerForm.includes('supp')) {
      return quantity === 1 ? 'stíll' : 'stílar';
    }
    // Drops: dropi (1), dropar (2+)
    // augndr = augndropar (eye drops), eyrnadr = eyrnadropar (ear drops)
    if (
      lowerForm.includes('drop') ||
      lowerForm.includes('dropa') ||
      lowerForm.includes('augndr') ||
      lowerForm.includes('eyrnadr')
    ) {
      return quantity === 1 ? 'dropi' : 'dropar';
    }
    // Eye ointment: augnhl = augnhlaup, augnsm = augnsmyrsli
    if (lowerForm.includes('augnhl') || lowerForm.includes('augnhlaup') || lowerForm.includes('augnsm')) {
      return 'áburður';
    }
    // Injections: sprauta/inndæling
    if (lowerForm.includes('spraut') || lowerForm.includes('inject') || lowerForm.includes('inj')) {
      return quantity === 1 ? 'sprauta' : 'sprautur';
    }
    // Inhalers: púst (same singular/plural)
    // innúðal = innúðalyf (inhalation solution)
    if (
      lowerForm.includes('innönd') ||
      lowerForm.includes('inhal') ||
      lowerForm.includes('púst') ||
      lowerForm.includes('úði') ||
      lowerForm.includes('innúðal')
    ) {
      return 'púst';
    }
    // Cream/ointment: áburður
    if (
      lowerForm.includes('smyr') ||
      lowerForm.includes('krem') ||
      lowerForm.includes('cream') ||
      lowerForm.includes('oint') ||
      lowerForm.includes('áburð')
    ) {
      return 'áburður';
    }
    // Liquid/solution: skammtur (1), skammtar (2+)
    if (
      lowerForm.includes('lausn') ||
      lowerForm.includes('mixtúr') ||
      lowerForm.includes('solution') ||
      lowerForm.includes('liquid')
    ) {
      return quantity === 1 ? 'skammtur' : 'skammtar';
    }
    // Default fallback
    return '';
  };

  // Get frequency text
  const getFrequencyText = (frequency: number): string => {
    switch (frequency) {
      case 1:
        return 'einu sinni á dag';
      case 2:
        return 'tvisvar sinnum á dag';
      case 3:
        return 'þrisvar sinnum á dag';
      case 4:
        return 'fjórum sinnum á dag';
      case 5:
        return 'fimm sinnum á dag';
      case 6:
        return 'sex sinnum á dag';
      case 7:
        return 'sjö sinnum á dag';
      case 8:
        return 'átta sinnum á dag';
      case 9:
        return 'níu sinnum á dag';
      case 10:
        return 'tíu sinnum á dag';
      case 11:
        return 'ellefu sinnum á dag';
      case 12:
        return 'tólf sinnum á dag';
      case 13:
        return 'þrettán sinnum á dag';
      case 14:
        return 'fjórtán sinnum á dag';
      case 15:
        return 'fimmtán sinnum á dag';
      case 16:
        return 'sextán sinnum á dag';
      case 17:
        return 'sautján sinnum á dag';
      case 18:
        return 'átján sinnum á dag';
      case 19:
        return 'nítján sinnum á dag';
      case 20:
        return 'tuttugu sinnum á dag';
      default:
        return `${frequency} sinnum á dag`;
    }
  };

  // Generate usage instructions from quantity, frequency, and medication form
  const generateUsageInstructions = (quantity: string, frequency: string, form: string | undefined): string => {
    const qty = Number.parseInt(quantity) || 1;
    const freq = Number.parseInt(frequency) || 1;
    const unitName = getUnitName(form, qty);

    if (unitName) {
      // "1 tafla einu sinni á dag" or "2 töflur tvisvar á dag"
      return `${qty} ${unitName} ${getFrequencyText(freq)}`;
    }
    // Fallback: "1 x 2 á dag"
    return `${qty} x ${freq} á dag`;
  };

  // Auto-generate usage instructions when quantity/frequency changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    const { quantity, frequency } = formData.frequencyDosage;
    if (quantity && frequency && formData.dosageFormat === 'frequency') {
      // Extract form from medication name since API embeds it there
      const medicationForm = formData.medication?.form || extractFormFromName(formData.medication?.name);
      const instructions = generateUsageInstructions(quantity, frequency, medicationForm);
      setFormData((prev) => ({
        ...prev,
        usageInstructions: instructions,
      }));
    }
  }, [
    formData.frequencyDosage.quantity,
    formData.frequencyDosage.frequency,
    formData.medication?.form,
    formData.medication?.name,
    formData.dosageFormat,
  ]);

  // Auto-search for medication based on AI-detected name
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!initialMedicationName || hasAutoSearched) return;

    const autoSearchMedication = async () => {
      // Extract just the medication name (remove dosage like "10 mg")
      const cleanName = initialMedicationName.replace(/\d+\s*(mg|mcg|ml|g)\b/gi, '').trim();

      if (cleanName.length < 2) return;

      setIsSearchingMedication(true);
      try {
        const response = await fetch(`/api/medication/search?query=${encodeURIComponent(cleanName)}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success && data.data && data.data.length > 0) {
          // Auto-select the first matching medication
          const firstMatch = data.data[0];

          // Determine "Notað við" - first try to extract from speech, then infer from medication
          let usedFor = '';
          if (initialDosageInfo) {
            const extracted = extractUsedFor(initialDosageInfo);
            if (extracted) {
              usedFor = extracted;
            }
          }
          // If not found in speech, try to extract from medication name itself
          if (!usedFor && initialMedicationName) {
            const extracted = extractUsedFor(initialMedicationName);
            if (extracted) {
              usedFor = extracted;
            }
          }
          // If still not found, infer from medication type
          if (!usedFor) {
            const inferred = inferConditionFromMedication(firstMatch.name || cleanName);
            if (inferred) {
              usedFor = inferred;
            }
          }

          // Parse dosage info if available
          const dosage = initialDosageInfo ? parseDosageInfo(initialDosageInfo) : null;

          setFormData((prev) => ({
            ...prev,
            medication: firstMatch,
            usedFor: usedFor || prev.usedFor,
            ...(dosage && {
              frequencyDosage: {
                ...prev.frequencyDosage,
                quantity: dosage.quantity,
                frequency: dosage.frequency,
              },
            }),
          }));
        }
      } catch (err) {
        console.error('Error auto-searching medication:', err);
      } finally {
        setIsSearchingMedication(false);
        setHasAutoSearched(true);
      }
    };

    autoSearchMedication();
  }, [initialMedicationName, initialDosageInfo, hasAutoSearched]);

  // Search medications (manual search)
  useEffect(() => {
    if (!medicationSearch.trim() || medicationSearch.length < 2) {
      setMedicationResults([]);
      return;
    }

    const searchMedications = async () => {
      setIsSearchingMedication(true);
      try {
        const response = await fetch(`/api/medication/search?query=${encodeURIComponent(medicationSearch)}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success && data.data) {
          setMedicationResults(data.data.slice(0, 10));
        }
      } catch (err) {
        console.error('Error searching medications:', err);
      } finally {
        setIsSearchingMedication(false);
      }
    };

    const timer = setTimeout(searchMedications, 300);
    return () => clearTimeout(timer);
  }, [medicationSearch]);

  // Search ICD10 codes
  useEffect(() => {
    if (!diagnosisSearch.trim() || diagnosisSearch.length < 2) {
      setDiagnosisResults([]);
      return;
    }

    const searchDiagnosis = async () => {
      setIsSearchingDiagnosis(true);
      try {
        const response = await fetch(`/api/icd10/search?query=${encodeURIComponent(diagnosisSearch)}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success && data.data) {
          setDiagnosisResults(data.data.slice(0, 10));
        }
      } catch (err) {
        console.error('Error searching ICD10:', err);
      } finally {
        setIsSearchingDiagnosis(false);
      }
    };

    const timer = setTimeout(searchDiagnosis, 300);
    return () => clearTimeout(timer);
  }, [diagnosisSearch]);

  // Update form field helper
  const updateField = <K extends keyof PrescriptionFormData>(field: K, value: PrescriptionFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submission
  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <div className="flex h-full flex-col">
      <ScrollArea className="flex-1">
        <div className="space-y-6 px-1 py-2">
          {/* Prescription Type */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Tegund lyfseðils</Label>
            <RadioGroup
              value={formData.prescriptionType}
              onValueChange={(v) => updateField('prescriptionType', v as PrescriptionType)}
              className="flex flex-wrap gap-2"
            >
              {[
                { value: 'fast', label: 'Fast' },
                { value: 'pn', label: 'PN' },
                { value: 'kur', label: 'Kúr' },
                { value: 'stofnun', label: 'Stofnun' },
              ].map((type) => (
                <div key={type.value} className="flex items-center">
                  <RadioGroupItem value={type.value} id={`type-${type.value}`} className="peer sr-only" />
                  <Label
                    htmlFor={`type-${type.value}`}
                    className={cn(
                      'cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors',
                      formData.prescriptionType === type.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background hover:bg-slate-100'
                    )}
                  >
                    {type.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </section>

          <Separator />

          {/* Medication Selection */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Lyf</Label>
            <Popover open={medicationOpen} onOpenChange={setMedicationOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  // biome-ignore lint/a11y/useSemanticElements: combobox pattern from shadcn/ui
                  role="combobox"
                  aria-expanded={medicationOpen}
                  className="h-auto min-h-10 w-full justify-between py-2"
                >
                  {formData.medication ? (
                    <div className="flex items-center gap-2 text-left">
                      <Pill className="h-4 w-4 shrink-0 text-primary" />
                      <span className="truncate">{formData.medication.name}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Leita að lyfi...</span>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Leita eftir nafni, virku efni eða númer..."
                    value={medicationSearch}
                    onValueChange={setMedicationSearch}
                  />
                  <CommandList>
                    {isSearchingMedication ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    ) : medicationResults.length === 0 ? (
                      <CommandEmpty>
                        {medicationSearch.length < 2 ? 'Sláðu inn a.m.k. 2 stafi...' : 'Ekkert lyf fannst.'}
                      </CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {medicationResults.map((med) => (
                          <CommandItem
                            key={med.id}
                            value={med.name}
                            onSelect={() => {
                              updateField('medication', med);
                              setMedicationOpen(false);
                              setMedicationSearch('');
                            }}
                            className="flex items-center gap-2"
                          >
                            <Pill className="h-4 w-4 text-muted-foreground" />
                            <div className="flex flex-col">
                              <span className="text-sm">{med.name}</span>
                              {med.nordic_number && (
                                <span className="text-muted-foreground text-xs">Vnr: {med.nordic_number}</span>
                              )}
                            </div>
                            {formData.medication?.id === med.id && <Check className="ml-auto h-4 w-4" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {formData.medication && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {formData.medication.name}
                  <button
                    type="button"
                    onClick={() => updateField('medication', null)}
                    className="ml-1 rounded-full p-0.5 hover:bg-slate-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}
          </section>

          <Separator />

          {/* Dosage Format */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Skammtaform</Label>
            <RadioGroup
              value={formData.dosageFormat}
              onValueChange={(v) => updateField('dosageFormat', v as DosageFormat)}
              className="flex flex-wrap gap-2"
            >
              {[
                { value: 'frequency', label: 'Tíðni' },
                { value: 'timeOfDay', label: 'Tímasetning' },
                ...(formData.prescriptionType === 'pn' ? [{ value: 'perNeed', label: 'Eftir þörfum' }] : []),
              ].map((format) => (
                <div key={format.value} className="flex items-center">
                  <RadioGroupItem value={format.value} id={`dosage-${format.value}`} className="peer sr-only" />
                  <Label
                    htmlFor={`dosage-${format.value}`}
                    className={cn(
                      'cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors',
                      formData.dosageFormat === format.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background hover:bg-slate-100'
                    )}
                  >
                    {format.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Frequency Dosage */}
            {formData.dosageFormat === 'frequency' && (
              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Magn</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.frequencyDosage.quantity}
                    onChange={(e) =>
                      updateField('frequencyDosage', {
                        ...formData.frequencyDosage,
                        quantity: e.target.value,
                      })
                    }
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Sinnum</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.frequencyDosage.frequency}
                    onChange={(e) =>
                      updateField('frequencyDosage', {
                        ...formData.frequencyDosage,
                        frequency: e.target.value,
                      })
                    }
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Á</Label>
                  <Select
                    value={formData.frequencyDosage.timeUnit}
                    onValueChange={(v) =>
                      updateField('frequencyDosage', {
                        ...formData.frequencyDosage,
                        timeUnit: v as TimeUnit,
                      })
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dag">dag</SelectItem>
                      <SelectItem value="viku">viku</SelectItem>
                      <SelectItem value="manudi">mánuði</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Time of Day Dosage */}
            {formData.dosageFormat === 'timeOfDay' && (
              <div className="grid grid-cols-4 gap-2 pt-2">
                {[
                  { key: 'morning', label: 'Morgun' },
                  { key: 'noon', label: 'Hádegi' },
                  { key: 'afternoon', label: 'Síðdegi' },
                  { key: 'evening', label: 'Kvöld' },
                ].map((time) => (
                  <div key={time.key} className="space-y-1">
                    <Label className="text-muted-foreground text-xs">{time.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.timeOfDayDosage[time.key as keyof typeof formData.timeOfDayDosage]}
                      onChange={(e) =>
                        updateField('timeOfDayDosage', {
                          ...formData.timeOfDayDosage,
                          [time.key]: e.target.value,
                        })
                      }
                      className="h-9"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Per Need Dosage */}
            {formData.dosageFormat === 'perNeed' && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Hámark á dag</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.perNeedDosage.maxPerDay}
                    onChange={(e) =>
                      updateField('perNeedDosage', {
                        ...formData.perNeedDosage,
                        maxPerDay: e.target.value,
                      })
                    }
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Eining</Label>
                  <Input
                    value={formData.perNeedDosage.unit}
                    onChange={(e) =>
                      updateField('perNeedDosage', {
                        ...formData.perNeedDosage,
                        unit: e.target.value,
                      })
                    }
                    placeholder="t.d. töflur"
                    className="h-9"
                  />
                </div>
              </div>
            )}
          </section>

          <Separator />

          {/* Usage Instructions */}
          <section className="space-y-2">
            <Label className="font-medium text-sm">Notkunarleiðbeiningar</Label>
            <Textarea
              value={formData.usageInstructions}
              onChange={(e) => updateField('usageInstructions', e.target.value)}
              placeholder="Leiðbeiningar um notkun lyfsins..."
              className="min-h-[60px] resize-none"
              maxLength={128}
            />
            <p className="text-right text-muted-foreground text-xs">{formData.usageInstructions.length}/128</p>
          </section>

          <Separator />

          {/* Packaging */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Pakkning</Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Fjöldi</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.packageQuantity}
                  onChange={(e) => updateField('packageQuantity', e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Mánuðir</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.months}
                  onChange={(e) => updateField('months', e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Dagar á milli</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.daysBetween}
                  onChange={(e) => updateField('daysBetween', e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Diagnosis (ICD10) */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Greining (ICD-10)</Label>
            <Popover open={diagnosisOpen} onOpenChange={setDiagnosisOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  // biome-ignore lint/a11y/useSemanticElements: combobox pattern from shadcn/ui
                  role="combobox"
                  aria-expanded={diagnosisOpen}
                  className="h-auto min-h-10 w-full justify-between py-2"
                >
                  {formData.diagnosis ? (
                    <div className="flex items-center gap-2 text-left">
                      <FileText className="h-4 w-4 shrink-0 text-primary" />
                      <span className="truncate">
                        {formData.diagnosis.icd_10} - {formData.diagnosis.name_is}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Leita að greiningu...</span>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Leita eftir ICD kóða eða lýsingu..."
                    value={diagnosisSearch}
                    onValueChange={setDiagnosisSearch}
                  />
                  <CommandList>
                    {isSearchingDiagnosis ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    ) : diagnosisResults.length === 0 ? (
                      <CommandEmpty>
                        {diagnosisSearch.length < 2 ? 'Sláðu inn a.m.k. 2 stafi...' : 'Engin greining fannst.'}
                      </CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {diagnosisResults.map((dx) => (
                          <CommandItem
                            key={dx.dx_id}
                            value={`${dx.icd_10} ${dx.name_is}`}
                            onSelect={() => {
                              updateField('diagnosis', dx);
                              setDiagnosisOpen(false);
                              setDiagnosisSearch('');
                            }}
                            className="flex items-start gap-2"
                          >
                            <Badge variant="outline" className="mt-0.5 shrink-0">
                              {dx.icd_10}
                            </Badge>
                            <div className="flex flex-col">
                              <span className="text-sm">{dx.name_is}</span>
                              <span className="text-muted-foreground text-xs">{dx.name_en}</span>
                            </div>
                            {formData.diagnosis?.dx_id === dx.dx_id && <Check className="ml-auto h-4 w-4" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {formData.diagnosis && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {formData.diagnosis.icd_10} - {formData.diagnosis.name_is}
                  <button
                    type="button"
                    onClick={() => updateField('diagnosis', null)}
                    className="ml-1 rounded-full p-0.5 hover:bg-slate-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}
          </section>

          <Separator />

          {/* Flags */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Valkostir</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rationing"
                  checked={formData.rationing}
                  onCheckedChange={(checked) => updateField('rationing', checked as boolean)}
                />
                <Label htmlFor="rationing" className="cursor-pointer text-sm">
                  Skömmtun (rationing)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="substitution"
                  checked={formData.substitutionAllowed}
                  onCheckedChange={(checked) => updateField('substitutionAllowed', checked as boolean)}
                />
                <Label htmlFor="substitution" className="cursor-pointer text-sm">
                  R-merking (skipta má um lyf)
                </Label>
              </div>
            </div>
          </section>

          <Separator />

          {/* Delivery */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Afhending</Label>
            <RadioGroup
              value={formData.deliveryType}
              onValueChange={(v) => updateField('deliveryType', v as DeliveryType)}
              className="flex flex-wrap gap-2"
            >
              {[
                { value: 'lyfjagatt', label: 'Lyfjagátt' },
                { value: 'apotek', label: 'Apótek' },
                { value: 'deliver', label: 'Heimsending' },
              ].map((delivery) => (
                <div key={delivery.value} className="flex items-center">
                  <RadioGroupItem value={delivery.value} id={`delivery-${delivery.value}`} className="peer sr-only" />
                  <Label
                    htmlFor={`delivery-${delivery.value}`}
                    className={cn(
                      'cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors',
                      formData.deliveryType === delivery.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background hover:bg-slate-100'
                    )}
                  >
                    {delivery.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Delivery Address */}
            {formData.deliveryType === 'deliver' && (
              <div className="space-y-3 border-primary/20 border-l-2 pt-2 pl-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs">Heimilisfang</Label>
                    <Input
                      value={formData.deliveryAddress.address}
                      onChange={(e) =>
                        updateField('deliveryAddress', {
                          ...formData.deliveryAddress,
                          address: e.target.value,
                        })
                      }
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs">Póstnúmer</Label>
                    <Input
                      value={formData.deliveryAddress.postalCode}
                      onChange={(e) =>
                        updateField('deliveryAddress', {
                          ...formData.deliveryAddress,
                          postalCode: e.target.value,
                        })
                      }
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Svæði</Label>
                  <Input
                    value={formData.deliveryAddress.area}
                    onChange={(e) =>
                      updateField('deliveryAddress', {
                        ...formData.deliveryAddress,
                        area: e.target.value,
                      })
                    }
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Athugasemd</Label>
                  <Textarea
                    value={formData.deliveryAddress.comment}
                    onChange={(e) =>
                      updateField('deliveryAddress', {
                        ...formData.deliveryAddress,
                        comment: e.target.value,
                      })
                    }
                    className="min-h-[40px] resize-none"
                  />
                </div>
              </div>
            )}
          </section>

          <Separator />

          {/* Validity */}
          <section className="space-y-3">
            <Label className="font-medium text-sm">Gildistími</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Gildir frá</Label>
                <Input
                  type="date"
                  value={formData.validFrom}
                  onChange={(e) => updateField('validFrom', e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Gildir til</Label>
                <Input
                  type="date"
                  value={formData.validTo}
                  onChange={(e) => updateField('validTo', e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Pharmacy Notes */}
          <section className="space-y-2">
            <Label className="font-medium text-sm">Athugasemdir til lyfjafræðings</Label>
            <Textarea
              value={formData.pharmacyNotes}
              onChange={(e) => updateField('pharmacyNotes', e.target.value)}
              placeholder="Athugasemdir..."
              className="min-h-[60px] resize-none"
            />
          </section>

          {/* Used For */}
          <section className="space-y-2">
            <Label className="font-medium text-sm">Notað við</Label>
            <Input
              value={formData.usedFor}
              onChange={(e) => updateField('usedFor', e.target.value)}
              placeholder="t.d. verkir, sýking..."
            />
          </section>
        </div>
      </ScrollArea>

      {/* Footer Actions */}
      <div className="flex gap-2 border-t pt-4">
        {onClose && (
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Hætta við
          </Button>
        )}
        <Button
          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          onClick={handleSubmit}
          disabled={!formData.medication}
        >
          <Check className="mr-1 h-4 w-4" />
          Staðfesta
        </Button>
      </div>
    </div>
  );
}
